import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { clearSessionCookie, hasSessionCookie, setSessionCookie } from '../../utils/session-cookie.utils';
import { EntityTypeEnum } from '../../enums/entity-type.enum';
import { SuccessErrorEnum } from '../../enums/success-error.enum';
import { ILoginIn } from '../../interfaces/auth.interface';
import { ISchoolSettings } from '../../interfaces/groups/school-settings.interface';
import { ISchool } from '../../interfaces/groups/school.interface';
import { IHttpError } from '../../interfaces/http-error.interface';
import { IUser } from '../../interfaces/users/user.interface';
import { AuthenticationService } from '../../services/authentication.service';
import {
    CreatePwd,
    GetMyUser,
    Login,
    Logout,
    PatchMyUser,
    RefreshTokens,
    ResetAuth,
    ResetFinalStepForgot,
    ResetPassword,
    ResetRememberMe,
    ResetSelectedSchool,
    SetCurrentUser,
    SetNewPwd,
    SetRememberMe,
    SetSelectedSchool,
} from '../actions/auth.action';
import { DeleteCachedClasses, GetAllClasses } from '../actions/classes.action';
import { ResetCloudData } from '../actions/cloud.actions';
import { GetStorageTypesEnum, SetInit } from '../actions/enums.action';
import { ResetEventsData } from '../actions/events.action';
import { DeleteCachedGroups, GetAllGroups } from '../actions/groups.action';
import { GetSchoolHomeworkSchoolSettings, ResetHomeworkSchoolData } from '../actions/homework-school.action';
import { GetSchoolMealsSettings, ResetMealsData } from '../actions/meals.action';
import { GetUnreadMessagesCount, ResetMessages } from '../actions/messaging.action';
import { GetNurserySettings, ResetNursery } from '../actions/nursery.action';
import { HasParentOrderedDuringActiveMonth, ResetOrdersDuringActiveMonth } from '../actions/orders.action';
import { ResetRepertoire } from '../actions/repertoire.action';
import { GetAllSchoolYear } from '../actions/school-year.action';
import { GetAllSchools, ResetSchools } from '../actions/schools.action';
import {
    DeleteCachedUsers,
    GetAllStudents,
    GetAllUsersLight,
    GetCustodies,
    ResetImpersonatedUser,
    SetIsImpersonated,
} from '../actions/users.action';
import { ResetWallet } from '../actions/wallet.action';

export interface AuthStateModel {
    rememberMe: boolean;
    isAuth: boolean;
    dashboardSelected: boolean;
    accessToken: string;
    refreshToken: string;
    errors: string[];
    finalStepForgot: SuccessErrorEnum;
    user: IUser;
    currentUser: IUser;
    currentSchool: ISchool;
    isLoading: boolean;
}

@State<AuthStateModel>({
    name: 'auth',
    defaults: {
        rememberMe: false,
        isAuth: false,
        dashboardSelected: false,
        accessToken: '',
        refreshToken: '',
        errors: [],
        user: null,
        currentUser: null,
        currentSchool: null,
        isLoading: false,
        finalStepForgot: null,
        // isAuth: true,
        // dashboardSelected: true,
        // stepForgot: 1,
        // accessToken:
        //     'eyJhbGciOiJIUzI1NiJ9.eyJ1c2VyX2lkIjoxLCJleHAiOjE2Mjc5OTk4NTEsImlhdCI6MTYyNzkxMzQ1MX0.xYiifMDva4UQ0I7ww2w4EMrtxH7Yo5Qx_240qcQN6qE',
        // refreshToken: 'sa6S-2HGAAby9f9V6nx0kg',
        // errors: [],
        // user: mockUsers[6],
        // currentUser: mockUsers[6],
        // currentSchool: mockUsers[6].school,
    },
})
@Injectable()
export class AuthState {
    @Selector()
    public static isAuthenticated(state: AuthStateModel): boolean {
        return state.isAuth;
    }
    @Selector()
    public static dashboardSelected(state: AuthStateModel): boolean {
        return state.dashboardSelected;
    }
    @Selector()
    public static getStudentIdSelected(state: AuthStateModel): number | null {
        const connectedUser: IUser = state.user;
        const selectedUser: IUser = state.currentUser;
        return connectedUser?.id && selectedUser?.id && connectedUser?.id !== selectedUser?.id ? selectedUser?.id : null;
    }
    @Selector()
    public static accessToken(state: AuthStateModel): string {
        return state.accessToken as string;
    }
    @Selector()
    public static refreshToken(state: AuthStateModel): string {
        return state.refreshToken as string;
    }
    @Selector()
    public static finalStepForgot(state: AuthStateModel): SuccessErrorEnum {
        return state.finalStepForgot;
    }
    @Selector()
    public static user(state: AuthStateModel): IUser {
        return state.user;
    }

    @Selector()
    public static connectedUserType(state: AuthStateModel): EntityTypeEnum {
        return state.user?.type;
    }

    @Selector()
    public static currentUserType(state: AuthStateModel): EntityTypeEnum {
        return state.currentUser?.type;
    }

    @Selector()
    public static isConnectedUserEmployee(state: AuthStateModel): boolean {
        return state.user?.type === EntityTypeEnum.EMPLOYEE;
    }

    @Selector()
    public static errors(state: AuthStateModel): string[] {
        return state.errors;
    }
    @Selector()
    public static currentUser(state: AuthStateModel): IUser {
        return state.currentUser;
    }

    @Selector()
    public static currentUserHasDivorcedParentsWithCustodyCalendar(state: AuthStateModel): boolean {
        return (
            state.currentUser.type === EntityTypeEnum.STUDENT &&
            state.currentUser?.settings?.parent_divorced &&
            state.currentUser?.settings?.costs_repartition_id === 1
        );
    }

    @Selector()
    public static currentUserHasDivorcedParentsWithProcentRepartition(state: AuthStateModel): boolean {
        return (
            state.currentUser.type === EntityTypeEnum.STUDENT &&
            state.currentUser?.settings?.parent_divorced &&
            state.currentUser?.settings?.costs_repartition_id === 2
        );
    }

    @Selector()
    public static currentSchool(state: AuthStateModel): ISchool {
        return state.currentSchool;
    }
    @Selector()
    public static currentSchoolId(state: AuthStateModel): number {
        return state.currentSchool.id;
    }

    @Selector()
    public static currentSchoolSettings(state: AuthStateModel): ISchoolSettings {
        return state.currentSchool?.settings;
    }

    @Selector()
    public static isSchoolSettingsLoaded(state: AuthStateModel): boolean {
        return state.currentSchool && !!state.currentSchool.settings;
    }

    @Selector()
    public static electronicPaymentEnabled(state: AuthStateModel): boolean {
        return state.currentSchool?.settings.shop_setting.electronic_payments;
    }

    @Selector()
    public static isLoading(state: AuthStateModel): boolean {
        return state.isLoading;
    }

    @Selector()
    public static rememberMe(state: AuthStateModel): boolean {
        return state.rememberMe;
    }

    @Selector()
    public static isImpersonated(state: AuthStateModel): boolean {
        return state.user?.id !== state.currentUser?.id;
    }

    @Selector()
    public static currentSchoolHasMediaSettingsActivated(state: AuthStateModel): boolean {
        return state.currentSchool?.settings?.medias_setting?.is_active;
    }

    constructor(
        private authService: AuthenticationService,
        private router: Router,
        private localize: LocalizeRouterService,
        private store: Store
    ) {}

    @Action(Login)
    public login(ctx: StateContext<AuthStateModel>, action: Login): Observable<any> {
        return this.authService.login(action.payload.email, action.payload.password).pipe(
            tap((result: ILoginIn) => {
                if (result.access_token) {
                    if (!action.payload.rememberMe) {
                        setSessionCookie();
                    }
                    ctx.patchState({
                        rememberMe: action.payload.rememberMe,
                        isAuth: true,
                        accessToken: result.access_token,
                        refreshToken: result.refresh_token,
                    });
                    ctx.dispatch(new GetMyUser());
                }
            })
        );
    }

    @Action(Logout)
    public logout(ctx: StateContext<AuthStateModel>): Observable<boolean> {
        return this.authService.logout().pipe(
            tap(() => {
                ctx.dispatch(new ResetAuth());
            }),
            catchError(() => {
                ctx.dispatch(new ResetAuth());
                return of(false);
            })
        );
    }

    @Action(ResetPassword)
    public resetPassword(ctx: StateContext<AuthStateModel>, action: ResetPassword): Observable<any> {
        return this.authService.resetPassword(action.email);
    }

    @Action(SetNewPwd)
    public setNewPwd(ctx: StateContext<AuthStateModel>, action: SetNewPwd): Observable<any> {
        const state: AuthStateModel = ctx.getState();
        return this.authService.setNewPwd(action.newPassword, action.token).pipe(
            tap(() => {
                ctx.patchState({
                    finalStepForgot: SuccessErrorEnum.SUCCESS,
                });
            }),
            catchError((err: IHttpError) => {
                ctx.patchState({
                    finalStepForgot: SuccessErrorEnum.ERROR,
                    errors: [...state.errors, err.error],
                });
                return of(err);
            })
        );
    }

    @Action(CreatePwd)
    public cretePwd(ctx: StateContext<AuthStateModel>, action: CreatePwd): Observable<any> {
        return this.authService.createPwd(action.password, action.token);
    }

    @Action(SetSelectedSchool)
    public setSelectedSchool(ctx: StateContext<AuthStateModel>, action: SetSelectedSchool): void {
        if (action.school) {
            ctx.patchState({
                dashboardSelected: true,
                currentSchool: action.school,
            });
            ctx.dispatch([
                new GetAllSchoolYear(),
                new GetSchoolMealsSettings(action.school.id),
                new GetNurserySettings(action.school.id),
                new GetSchoolHomeworkSchoolSettings(action.school.id),
                new GetUnreadMessagesCount(),
                new GetAllClasses(),
                new GetAllUsersLight(),
                new GetAllGroups(),
            ]);
        }
    }

    @Action(SetCurrentUser)
    public setCurrentUser(ctx: StateContext<AuthStateModel>, action: SetCurrentUser): void {
        if (action.user) {
            ctx.patchState({
                dashboardSelected: true,
                currentUser: action.user,
            });
            if (action.user.type === EntityTypeEnum.STUDENT) {
                ctx.dispatch(new ResetImpersonatedUser(action.user.id));
            }
            ctx.dispatch(new SetIsImpersonated(this.isImpersonated(ctx.getState())));
            if (
                action.user.type === EntityTypeEnum.STUDENT &&
                action.user.settings.parent_divorced &&
                action.user.settings.costs_repartition_id === 1
            ) {
                ctx.dispatch(new GetCustodies(action.user.id));
            }
        }
    }

    @Action(GetMyUser)
    public getMyUser(ctx: StateContext<AuthStateModel>): Observable<IUser> {
        const state: AuthStateModel = ctx.getState();
        return this.authService.getMyUser().pipe(
            tap((response: IUser) => {
                ctx.patchState({
                    user: response,
                });

                if (response.type === EntityTypeEnum.STUDENT) {
                    ctx.dispatch(new ResetImpersonatedUser(response.id));
                }
                ctx.dispatch(new SetIsImpersonated(this.isImpersonated(ctx.getState())));

                if (response.type === EntityTypeEnum.PARENT) {
                    ctx.dispatch([
                        new GetAllStudents(),
                        new HasParentOrderedDuringActiveMonth(response.hasHomeworkOrders, response.hasMealOrders),
                    ]);
                }
                if (response.type === EntityTypeEnum.ADMINISTRATOR) {
                    ctx.dispatch(new GetStorageTypesEnum());
                }
                ctx.dispatch(new GetAllSchools()).subscribe(
                    () => {
                        ctx.patchState({
                            isLoading: false,
                        });
                        if (state.currentSchool) {
                            ctx.dispatch([
                                new GetAllUsersLight(),
                                new GetAllSchoolYear(),
                                new GetNurserySettings(state.currentSchool.id),
                                new GetSchoolMealsSettings(state.currentSchool.id),
                                new GetSchoolHomeworkSchoolSettings(state.currentSchool.id),
                                new GetUnreadMessagesCount(),
                                new GetAllClasses(),
                                new GetAllGroups(),
                            ]);
                        }
                        ctx.dispatch([new SetInit()]);
                    },
                    () => {
                        ctx.dispatch(new ResetAuth());
                        setTimeout(() => {
                            this.router.navigate([this.localize.translateRoute('/auth/login')]);
                        }, 0);
                    }
                );
            })
        );
    }

    @Action(RefreshTokens)
    public refreshTokens(ctx: StateContext<AuthStateModel>): Observable<ILoginIn | void> {
        const state: AuthStateModel = ctx.getState();
        ctx.patchState({
            isLoading: true,
        });
        if (!state.rememberMe && !hasSessionCookie()) {
            return ctx.dispatch(new ResetAuth());
        }
        return this.authService.refreshTokens(ctx.getState().refreshToken).pipe(
            tap((response: ILoginIn) => {
                ctx.patchState({
                    isAuth: true,
                    accessToken: response.access_token,
                    refreshToken: response.refresh_token,
                });
                ctx.dispatch(new GetMyUser());
            }),
            catchError((e: any) => {
                ctx.dispatch(new ResetAuth());
                return of(e);
            })
        );
    }

    @Action(ResetFinalStepForgot)
    public resetFinalStepForgot(ctx: StateContext<AuthStateModel>): void {
        ctx.patchState({
            finalStepForgot: null,
        });
    }

    @Action(ResetAuth)
    public resetAuth(ctx: StateContext<AuthStateModel>): void {
        clearSessionCookie();
        ctx.patchState({
            dashboardSelected: false,
            rememberMe: false,
            isAuth: false,
            accessToken: '',
            refreshToken: '',
            errors: [],
            user: null,
            currentUser: null,
            currentSchool: null,
            isLoading: false,
            finalStepForgot: null,
        });
        this.store.dispatch([
            new ResetMealsData(),
            new ResetHomeworkSchoolData(),
            new ResetMessages(),
            new ResetRepertoire(),
            new ResetNursery(),
            new ResetWallet(),
            new ResetSchools(),
            new DeleteCachedUsers(),
            new ResetEventsData(),
            new ResetCloudData(),
            new ResetOrdersDuringActiveMonth(),
        ]);
    }

    @Action(PatchMyUser)
    public patchMyUser(ctx: StateContext<AuthStateModel>, action: PatchMyUser): AuthStateModel {
        return ctx.patchState({
            user: action.myUser,
        });
    }

    @Action(ResetSelectedSchool)
    public resetSelectedSchool(ctx: StateContext<AuthStateModel>): void {
        ctx.dispatch([
            new ResetCloudData(),
            new ResetRepertoire(),
            new DeleteCachedGroups(),
            new DeleteCachedClasses(),
            new DeleteCachedUsers(),
        ]);
        ctx.patchState({
            currentSchool: null,
            dashboardSelected: false,
            currentUser: null,
        });
    }

    @Action(SetRememberMe)
    public setRememberMe(ctx: StateContext<AuthStateModel>): void {
        ctx.patchState({
            rememberMe: true,
        });
    }

    @Action(ResetRememberMe)
    public resetRememberMe(ctx: StateContext<AuthStateModel>): void {
        ctx.patchState({
            rememberMe: false,
        });
    }

    private isImpersonated(state: AuthStateModel): number {
        const user: IUser = state.user;
        const impersonated: IUser = state.currentUser;
        if (!user) return null;
        return user && impersonated && user?.id !== impersonated?.id ? impersonated.id : null;
    }
}
